'use client';

import React from 'react';

import Image from 'next/image';
import Chip from '@/components/global/ui/Chip';
import DecorationLine from '@/components/global/ui/DecorationLine';
import FAQList from '@/components/global/ui/FAQList';
import { cn } from '@/lib/utils';
import { PortableText } from '@portabletext/react';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { GROQCornerButton, GROQImage } from '@/sanity/lib/definitions';
import { PortableTextBlock } from 'sanity';
import { CTACornerButton } from '@/shadcn/ui/corner-button';

export type FAQContact = {
  image?: GROQImage;
  name?: string;
  position?: string;
  email?: string;
};

/*
  Opt-in card naming a person to write to. It sits under the heading on desktop and
  below the question list on mobile, so it is rendered in both slots and shown one at
  a time rather than moved with CSS — the two places are in different columns.
*/
function FAQContactCard({
  contact,
  className,
}: {
  contact?: FAQContact;
  className?: string;
}) {
  if (!contact?.name && !contact?.email) return null;

  return (
    <div
      className={cn(
        'items-center gap-[3rem] lg:w-[45rem] border-l-[0.5rem] border-[#CAF3A2] bg-white py-[1rem] pl-[1rem] pr-[7.5rem] relative',
        className,
      )}
    >
      <div className="absolute rotate-45 w-[7rem] h-[4rem] bg-[#f3f2f1] top-[-1rem] right-[-2.2rem] "></div>
      {contact?.image?.src && (
        <div className="relative h-[16rem] w-[16rem] shrink-0 overflow-clip">
          <Image
            className="absolute left-0 top-0 !h-full !w-full object-cover"
            src={contact.image}
            alt={contact?.name || ''}
          />
        </div>
      )}

      {/* the copy is spread over the photo's height, as drawn */}
      <div className="flex flex-col items-start justify-between self-stretch py-[1.625rem]">
        <div className="flex flex-col items-start gap-[1rem]">
          {contact?.name && (
            <p className="mb-0 whitespace-nowrap font-funnel text-[2.5rem] font-medium leading-none text-[#0A1A1D] [text-box-edge:cap_alphabetic] [text-box-trim:trim-both]">
              {contact.name}
            </p>
          )}

          {contact?.position && (
            <p className="mb-0 whitespace-nowrap font-geist text-[2rem] leading-[1.4] text-[#0A1A1D] opacity-60">
              {contact.position}
            </p>
          )}
        </div>

        {contact?.email && (
          <a
            className="font-geist text-[2rem] leading-[1.4] text-[#0A1A1D] underline opacity-60"
            href={`mailto:${contact.email}`}
          >
            {contact.email}
          </a>
        )}
      </div>
    </div>
  );
}

export default function FAQSection({
  chip,
  text,
  cta,
  items,
  contact,
  showContact = false,
  accordion = false,
  className,
  containerClassName,
}: {
  chip?: {
    icon: GROQImage;
    text: string;
  };
  text?: PortableTextBlock[];
  cta?: GROQCornerButton;
  items: {
    question: string;
    answer: PortableTextBlock[];
  }[];
  contact?: FAQContact;
  /** off by default — only the pages whose design calls for the card opt in */
  showContact?: boolean;
  /** collapsible list with a plus/minus toggle; off by default */
  accordion?: boolean;
  className?: string;
  containerClassName?: string;
}) {
  return (
    <section
      className={cn(
        'relative overflow-clip  lg:py-[15rem] py-[8rem] ',
        className,
      )}
    >
      <DecorationLine
        className="top-0"
        orientation="vertical"
        position="left"
      />
      <DecorationLine
        className="top-0"
        orientation="vertical"
        position="right"
      />

      <div className="container flex flex-col items-center">
        <div
          className={cn(
            'w-[min(187.5rem,100%)] flex flex-col lg:flex-row items-center lg:items-start lg:justify-between gap-[4rem]',
            containerClassName,
          )}
        >
          <div className="w-[min(63.25rem,100%)] flex flex-col items-center lg:items-start lg:sticky lg:top-[15rem]">
            {chip && <Chip image={chip?.icon} text={chip.text} />}

            {text && (
              <div className="richtext lg:richtext-start">
                <PortableText value={text} components={richTextComponent} />
              </div>
            )}

            {cta && <CTACornerButton className="mt-[3rem]" data={cta} />}

            {showContact && (
              <FAQContactCard
                className="mt-[4rem] hidden w-full lg:flex"
                contact={contact}
              />
            )}
          </div>

          <div className="w-[min(91.875rem,100%)]">
            {items && (
              <FAQList className="w-full" faq={items} accordion={accordion} />
            )}

            {showContact && (
              <FAQContactCard
                className="mt-[2.75rem] flex w-full lg:hidden"
                contact={contact}
              />
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
